import {
  DeleteFileS3Result,
  deleteServiceRequestFileFromS3,
  prepareServiceRequestPayload,
  separateAttachments,
  UploadResult,
  uploadServiceRequestFileToS3,
} from 'src/components/ServiceRequestForm/ServiceRequestFormDetails/ServiceRequestFormDetailsCustomHooks/helpers';
import { toast } from 'react-toastify';
import { addFaqsNotesDocuments, saveServiceRequest } from 'src/services/client';
import { useDispatch } from 'react-redux';
import { fetchSuiteCounts } from 'src/store/actions/suites';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { store } from 'src/store';
import { fetchPropertyCounts } from 'src/store/actions/properties';
import {
  clearServiceRequestFormData,
  setServiceRequestFormNextStep,
  setServiceRequestFormStep,
} from 'src/store/actions/serviceRequestForm';
import { useState } from 'react';
import {
  ServiceRequestFormData,
  ServiceRequestPayload,
} from 'src/components/ServiceRequestForm/ServiceRequestFormDetails/ServiceRequestFormDetailsCustomHooks/serviceRequestFormtypes';

export const useServiceRequestSubmission = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();

  // Todo: Migrate this away from Redux (Spaghetti Redux)
  const handleAddAnother = (from: string, serviceRequestPayload: any) => {
    if (from === 'Property') {
      dispatch(
        clearServiceRequestFormData({
          property_id: serviceRequestPayload.propertyId,
          propertyId: serviceRequestPayload.propertyId,
        })
      );
    } else if (from === 'Suite') {
      dispatch(
        clearServiceRequestFormData({
          property_id: serviceRequestPayload.propertyId,
          propertyId: serviceRequestPayload.propertyId,
          suite_id: serviceRequestPayload.suiteId,
          suiteId: serviceRequestPayload.suiteId,
        })
      );
    } else {
      dispatch(clearServiceRequestFormData({}));
    }
    dispatch(setServiceRequestFormStep(0));
  };

  const rollbackUploadedS3Files = async (uploadedFiles: string[]) => {
    if (uploadedFiles.length === 0) return;

    console.error('Rolling back uploaded files:', uploadedFiles);

    const deletePromises = uploadedFiles.map(async (fileUrl: string) => {
      const deleteResult: DeleteFileS3Result =
        await deleteServiceRequestFileFromS3(fileUrl);

      if (deleteResult.success === false) {
        console.error('Failed to delete:', fileUrl, deleteResult.error);
      }
    });

    await Promise.all(deletePromises);

    console.error(
      "' 🚫 Service request creation failed. Uploaded files have been rolled back.'"
    );
  };

  /**
   * Core service request creation logic with rollback support.
   * Uploads files to S3, creates the service request, and rolls back files if SR creation fails.
   * @returns The created service request data
   * @throws Error if upload or SR creation fails (triggers a doc rollback)
   */
  const createServiceRequestWithAttachedFiles = async (
    formData: ServiceRequestFormData
  ) => {
    const uploadedFilesForRollback: string[] = [];

    try {
      const srAttachments: any = formData.srAttachments || [];
      const { filesToUpload, existingUrls } =
        separateAttachments(srAttachments);

      const uploadedUrls: string[] = [...existingUrls];

      // Upload new files to S3.
      for (const file of filesToUpload) {
        const uploadResult: UploadResult =
          await uploadServiceRequestFileToS3(file);

        if (uploadResult.success === false) {
          toast.error(`Upload failed: ${uploadResult.error}`);
          throw new Error(uploadResult.error);
        }

        uploadedUrls.push(uploadResult.url);
        uploadedFilesForRollback.push(uploadResult.url);
      }

      // Create the service request on the API.
      const serviceRequestPayload: ServiceRequestPayload =
        prepareServiceRequestPayload(formData, uploadedUrls);

      const response: any = await saveServiceRequest(serviceRequestPayload);

      if (!response.ok || response.status !== 200) {
        throw new Error('Failed to create service request');
      }

      const responseJson = await response.json();

      if (responseJson.statusCode !== 200) {
        throw new Error('Service request creation failed');
      }

      toast.success('ⓘ Service request created successfully.', {
        autoClose: 12000,
        position: 'top-center',
      });

      return {
        srData: responseJson.data[0],
        responseJson,
        serviceRequestPayload,
      };
    } catch (err: any) {
      await rollbackUploadedS3Files(uploadedFilesForRollback);
      console.error('Error during service request creation:', err);
      toast.error(
        ' 🚫 Something went wrong with adding a service request, please try again.',
        { autoClose: 12000, position: 'top-center' }
      );
      throw err;
    }
  };

  const addDocumentsToServiceRequest = async (
    attachments: any,
    responseJson: any,
    serviceRequestPayload: any
  ) => {
    try {
      if (attachments && attachments.length > 0) {
        let images = JSON.parse(attachments);

        // This piece of legacy code was just migrated from BOS for now from ServiceRequestFormDetails.
        if (images.length > 0) {
          for (var i = 0; i < images.length; i++) {
            let doc = {
              name: 'Document' + i.toString(),
              description: 'through service request',
              type: 'Image',
              document: images[i],
            };
            let data = {
              ItemId: responseJson.data[0].id,
              Name: 'DOCUMENTS',
              Type: 'DOCUMENTS',
              Data: JSON.stringify(doc).toString(),
              UserId: serviceRequestPayload.creatorId,
            };

            await addFaqsNotesDocuments(data);
          }
        }
      }
    } catch (err: any) {
      console.error('Error adding documents to service request:', err);
      toast.error(
        ' 🚫 Something went wrong with adding documents to service request, please go to the created Service Request and try upload them again .',
        { autoClose: 12000, position: 'top-center' }
      );
    }
  };

  /* Post-creation operations that run after SR is successfully created.
   * These operations do NOT trigger file rollback if they fail since the SR already exists. */
  const handlePostCreationOperations = async (
    srData: any,
    responseJson: any,
    serviceRequestPayload: any,
    type: any,
    from: any
  ) => {
    try {
      const attachments: any = srData.attachment || '';
      await addDocumentsToServiceRequest(
        attachments,
        responseJson,
        serviceRequestPayload
      );

      /** 🛑 REFACTOR: Decouple Count + 'easyWalkThroughFormData' Logic (Spaghetti Redux) */
      if (from === 'Suite') {
        dispatch(fetchSuiteCounts(serviceRequestPayload.suiteId));
      }

      if (from === 'Property') {
        let user = selectAuthenticatedUser(store.getState());
        dispatch(
          fetchPropertyCounts(
            serviceRequestPayload.propertyId,
            user.id,
            user.roles[0].role.name
          )
        );
      }

      if (type === 'AddAnother') {
        handleAddAnother(from, serviceRequestPayload);
      } else {
        // Default flow: store in localStorage and go to next step (leftover legacy from BOS).
        localStorage.setItem('ticketId', srData.ticketId);
        localStorage.setItem('ticketNumber', srData.ticketNumber);
        localStorage.setItem('serviceRequestId', srData.id);
        dispatch(setServiceRequestFormNextStep());
      }
    } catch (err: any) {
      // Log but don't block - SR was created successfully.
      console.error('Error in post-creation operations:', err);
      toast.error(
        ' 🚫 Something went wrong after creating service request, please check the details.',
        err
      );
    }
  };

  const submitServiceRequest = async (
    formData: ServiceRequestFormData,
    type: any,
    from: any
  ) => {
    setIsSubmitting(true);

    try {
      const { srData, responseJson, serviceRequestPayload } =
        await createServiceRequestWithAttachedFiles(formData);

      await handlePostCreationOperations(
        srData,
        responseJson,
        serviceRequestPayload,
        type,
        from
      );

      return {
        success: true,
        data: srData,
        fullResponse: responseJson,
      };
    } catch (err: any) {
      console.error('Service request submission failed:', err);
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitServiceRequest,
    isSubmitting,
  };
};
